import type { CoviChatItem } from "../model/types"
import { parseProposedActions } from "./actions"

export function isStorageRecord(
  value: unknown,
): value is Record<string, unknown> {
  return value != null && typeof value === "object"
}

function isValidActionStatus(value: unknown): boolean {
  return (
    value === "proposed" ||
    value === "executing" ||
    value === "done" ||
    value === "error" ||
    value === "dismissed"
  )
}

function isValidChatItem(item: unknown): item is CoviChatItem {
  if (!isStorageRecord(item) || typeof item.id !== "string") {
    return false
  }

  switch (item.kind) {
    case "user":
    case "assistant":
    case "error":
      return typeof item.text === "string"
    case "tool":
      return (
        typeof item.name === "string" &&
        typeof item.summary === "string" &&
        typeof item.running === "boolean" &&
        typeof item.isError === "boolean"
      )
    case "action":
      return (
        parseProposedActions([item.proposal]) !== null &&
        typeof item.proposedAt === "number" &&
        Number.isFinite(item.proposedAt) &&
        isValidActionStatus(item.status) &&
        (item.detail === undefined || typeof item.detail === "string") &&
        (item.href === undefined || typeof item.href === "string") &&
        (item.error === undefined || typeof item.error === "string")
      )
    default:
      return false
  }
}

export function sanitizeHydratedItems(items: unknown[]): CoviChatItem[] {
  const sanitized: CoviChatItem[] = []
  for (const item of items) {
    if (!isValidChatItem(item)) {
      continue
    }
    if (item.kind === "tool") {
      sanitized.push(
        typeof item.status === "string" ? item : { ...item, status: "unknown" },
      )
      continue
    }
    if (item.kind !== "action") {
      sanitized.push(item)
      continue
    }
    if (parseProposedActions([item.proposal]) === null) {
      continue
    }
    if (item.status === "executing") {
      sanitized.push({
        ...item,
        status: "error",
        error:
          "Interrupted — the panel reloaded while this action was running.",
      })
      continue
    }
    sanitized.push(item)
  }
  return sanitized
}
